"use client";
import { TbCameraPlus } from "react-icons/tb";
import Spinner from "../themed/spinner";
import { ChangeEvent, useCallback, useRef, useState } from "react";
import { api } from "~/utils/api";
import axios from "axios";
import { cn } from "~/utils/fontsConfig";
import { toast } from "react-toastify";
import { Button } from "../v3/themed/Button";
import React from "react";
import { AcceptType, DEFAULT_IMAGES } from "~/utils/images";
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "../v3/shadcn/Sheet";
import Cropper, { Area } from "react-easy-crop";
import { LuX } from "react-icons/lu";
import { Slider } from "../v3/shadcn/Slider";

function mergeRefs(...refs: React.ForwardedRef<HTMLInputElement>[]) {
    return (instance: HTMLInputElement | null) => {
        refs.forEach((ref) => {
            if (typeof ref === "function") {
                ref(instance);
            } else if (ref != null) {
                ref.current = instance;
            }
        });
    };
}

type IterableNumberIndexed<T> = {
    [index: number]: T;
    length: number;
    [Symbol.iterator](): Iterator<T>;
};

export interface InputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "accept"> {
    onUploadComplete: (filename: string) => void;
    onLoadingChange?: (isLoading: boolean) => void;
    bucketKey: string;
    isCentered?: boolean;
    multi?: boolean;
    className?: string;
    variant?: "standard" | "button";
    cropBeforeUpload?: boolean;
    accept?: AcceptType | AcceptType[] | string | undefined;
    setIsUploading?: (isUploading: boolean) => void;
    disabled?: boolean;
    children?: React.ReactNode;
    showProgress?: boolean;
    maxFileSize?: number;
}

const UploadImage = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            onUploadComplete,
            onLoadingChange,
            bucketKey,
            isCentered,
            multi,
            className,
            variant,
            cropBeforeUpload,
            accept,
            setIsUploading,
            disabled,
            children,
            showProgress,
            maxFileSize = 20 * 1024 * 1024, // 20MB in bytes
        },
        ref,
    ) => {
        const fileInputRef = useRef<HTMLInputElement | null>(null);
        const [isLoading, setIsLoading] = useState(false);
        const [imageSrc, setImageSrc] = useState<string | null>(null);
        let combinedAccept: string = DEFAULT_IMAGES;
        const [isCropSheetOpen, setIsCropSheetOpen] = useState(false);

        if (accept) {
            let normalized: AcceptType[] | undefined = undefined;
            if (typeof accept === "string") combinedAccept = accept;
            else if (!(accept instanceof Array)) normalized = [accept];
            else normalized = accept;
            if (normalized) {
                combinedAccept = normalized.join(", ");
            }
        }

        const clsCentered =
            "inset-x-1/2 inset-y-1/2 -translate-x-1/2 -translate-y-1/2";

        const { mutateAsync: fetchPresignedUrls } =
            api.s3.getStandardUploadPresignedUrl.useMutation();

        const uploadFiles = async (files: IterableNumberIndexed<File>) => {
            for (let i = 0; i < files.length; i++) {
                const toastId = `upload-image-${i}`;
                if (files.length === 1) {
                    toast.loading("Uploading image", { toastId, progress: 0 });
                } else {
                    toast.loading(`Uploading image ${i + 1}/${files.length}`, {
                        toastId,
                        progress: 0,
                    });
                }
                setIsLoading(true);
                setIsUploading?.(true);
                onLoadingChange && onLoadingChange(true);
                const file = files[i]!;
                const ext = file.name.substring(file.name.lastIndexOf("."));
                const uploadFilename = `${(Math.random() + 1)
                    .toString(36)
                    .substring(7)}${ext}`;

                const presignedURL = await fetchPresignedUrls({
                    key: `${bucketKey}/${uploadFilename}`,
                });

                let newFile = new File([file], uploadFilename, {
                    type: file.type,
                });

                const resp = await axios
                    .put(presignedURL, newFile.slice(), {
                        headers: { "Content-Type": newFile.type },
                        onUploadProgress: (progressEvent) => {
                            if (progressEvent.total) {
                                const percentCompleted = Math.round(
                                    (progressEvent.loaded * 100) /
                                        progressEvent.total,
                                );
                                toast.update(`upload-image-${i}`, {
                                    progress: percentCompleted / 100,
                                });
                            }
                        },
                    })
                    .catch((err) => console.error(err));

                await onUploadComplete(uploadFilename);
                toast.dismiss(`upload-image-${i}`);
            }
            toast.success(multi ? "Images updated!" : "Image updated!");
            setIsLoading(false);
            setIsUploading?.(false);
            onLoadingChange && onLoadingChange(false);
        };

        const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
            if (!e.target.files || !e.target.files[0]) return;
            if (maxFileSize) {
                for (let i = 0; i < e.target.files.length; i++) {
                    if (e.target.files[i]!.size > maxFileSize) {
                        toast.error("File size is too large");
                        return;
                    }
                }
            }
            if (cropBeforeUpload) {
                const imageDataUrl = await readFile(e.target.files[0]);
                setImageSrc(imageDataUrl);
                setIsCropSheetOpen(true);
                return;
            }
            await uploadFiles(e.target.files);
        };

        if (variant === "button" || children) {
            return (
                <>
                    <Button
                        className={cn("", isCentered && clsCentered, className)}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={disabled}
                        type="button"
                    >
                        {children ? (
                            children
                        ) : (
                            <>
                                <TbCameraPlus
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                />
                                {multi ? "Upload Images" : "Upload Image"}
                            </>
                        )}
                        <input
                            ref={mergeRefs(fileInputRef, ref)}
                            type="file"
                            onChange={handleFileChange}
                            multiple={multi || false}
                            accept={combinedAccept}
                            hidden
                        />
                    </Button>
                    <CropSheet
                        imageSrc={imageSrc ?? ""}
                        open={isCropSheetOpen}
                        onOpenChange={setIsCropSheetOpen}
                        handleFilesUpload={uploadFiles}
                    />
                </>
            );
        }
        return (
            <div
                className={cn(
                    `
              absolute
              inline-block
              flex
              h-14
              w-14
              cursor-pointer
              items-center
              justify-center
              rounded-full
              bg-foreground/40
              shadow-lg
              shadow-background/60
              backdrop-blur-sm
              transition-[background-color]
              transition-shadow
              hover:bg-foreground/60
              hover:shadow-xl
              hover:shadow-background
            `,
                    isCentered && clsCentered,
                    className,
                )}
                onClick={() => fileInputRef.current?.click()}
            >
                {isLoading ? (
                    <Spinner />
                ) : (
                    <TbCameraPlus
                        className="h-6 w-6 text-foreground"
                        aria-hidden="true"
                    />
                )}

                <input
                    ref={mergeRefs(fileInputRef, ref)}
                    type="file"
                    onChange={handleFileChange}
                    accept={combinedAccept}
                    hidden
                />
            </div>
        );
    },
);

const CropSheet = ({
    imageSrc,
    open,
    onOpenChange,
    handleFilesUpload,
}: {
    imageSrc: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    handleFilesUpload: (files: File[]) => void;
}) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
        null,
    );

    const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleAccept = useCallback(async () => {
        const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels!);
        if (!croppedImage) throw new Error("Unable to crop the file");
        const file = new File(
            [croppedImage as any], // cast as any
            "cropped-file.png",
            {
                lastModified: new Date().getTime(),
                type: croppedImage.type,
            },
        );
        await handleFilesUpload([file]);
        onOpenChange(false);
    }, [imageSrc, croppedAreaPixels]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="top"
                showCloseIcon={false}
                className="z-[2500] w-full overflow-y-scroll rounded-br-md rounded-tr-md border-none bg-[#121418] pb-20 md:min-w-[450px]"
            >
                <SheetHeader>
                    <div className="flex w-full items-center justify-between px-6 py-4">
                        <SheetTitle className="text-lg">Crop Image</SheetTitle>

                        <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>
                </SheetHeader>
                <div className="flex flex-col p-6">
                    <div className="relative h-[50vh] w-full">
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={16 / 9}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </div>
                    <div className="flex w-full gap-8">
                        <div className="flex w-full justify-center">
                            <div className="flex w-full max-w-xl items-center justify-center gap-4">
                                Zoom
                                <Slider
                                    value={[zoom]}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    aria-labelledby="Zoom"
                                    onValueChange={(val) =>
                                        setZoom(val[0] ?? 0)
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 py-6">
                            <Button
                                variant="secondary"
                                onClick={() => handleAccept()}
                            >
                                Accept
                            </Button>
                            <Button
                                variant="tertiary"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

const readFile = async (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener(
            "load",
            () => resolve(reader.result as string),
            false,
        );
        reader.readAsDataURL(file);
    });
};

const createImage = async (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener("load", () => resolve(image));
        image.addEventListener("error", (error) => reject(error));
        image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
        image.src = url;
    });

const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area,
    flip = { horizontal: false, vertical: false },
): Promise<Blob | null> => {
    const image: HTMLImageElement = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        return null;
    }

    const MAX_SIZE = 2048;
    const scale =
        Math.max(pixelCrop.width, pixelCrop.height) < MAX_SIZE
            ? 1
            : MAX_SIZE / Math.max(pixelCrop.width, pixelCrop.height);

    // set canvas size to match the bounding box
    // and resize huge images
    canvas.width = image.width;
    canvas.height = image.height;

    // draw rotated image
    ctx.drawImage(image, 0, 0);

    const croppedCanvas = document.createElement("canvas");
    const croppedCtx = croppedCanvas.getContext("2d");

    if (!croppedCtx) return null;
    // Set the size of the cropped canvas
    croppedCanvas.width = Math.round(pixelCrop.width * scale);
    croppedCanvas.height = Math.round(pixelCrop.height * scale);

    // Draw the cropped image onto the new canvas
    croppedCtx.drawImage(
        canvas,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        Math.round(pixelCrop.width * scale),
        Math.round(pixelCrop.height * scale),
    );

    // As a blob
    return new Promise((resolve, reject) => {
        croppedCanvas.toBlob((file) => {
            resolve(file);
        }, "image/png");
    });
};

export default UploadImage;
